import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore, DatastoreCollection } from '@freelancer/datastore';
import { Task, TasksCollection } from '@freelancer/datastore/collections';
import { LocalStorage } from '@freelancer/local-storage';
import { ModalService } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ContainerSize } from '@freelancer/ui/container';
import { Margin } from '@freelancer/ui/margin';
import { ModalSize } from '@freelancer/ui/modal';
import { BackgroundColor, LayoutType } from '@freelancer/ui/page-layout';
import { toNumber } from '@freelancer/utils';
import {
  TaskSourceTypeApi,
  TaskStatusApi,
} from 'api-typings/tasklist/tasklist';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import {
  TaskError,
  TaskErrorAction,
  TaskErrorRemedy,
} from './task-error/task-error.model';
import {
  TaskModalAction,
  TaskModalComponent,
  TaskModalResult,
} from './task-modal/task-modal.component';

export const TASK_LIST_KEY = 'taskList';

@Component({
  selector: 'app-tasklist',
  template: `
    <fl-bit class="PageContainer" flTrackingSection="Tasklist">
      <fl-container [size]="pageSize">
        <ng-container *ngIf="formError$ | async as formErr">
          <fl-banner-alert
            *ngIf="formErr.show"
            bannerTitle="Something has gone wrong!"
            i18n-bannerTitle="Task error title"
            [type]="BannerAlertType.ERROR"
            [flMarginBottom]="Margin.SMALL"
            [closeable]="false"
          >
            <ng-container [ngSwitch]="formErr.action">
              <ng-container
                *ngSwitchCase="TaskErrorAction.TASK_TOGGLE"
                i18n="Tasks error message"
              >
                It looks like something has gone wrong while trying to toggle a
                task.
              </ng-container>
              <ng-container
                *ngIf="formErr.action === TaskErrorAction.TASK_CREATE"
                i18n="Tasks error message"
              >
                It looks like something has gone wrong while trying to create a
                task.
              </ng-container>
              <ng-container
                *ngIf="formErr.action === TaskErrorAction.TASK_DELETE"
                i18n="Tasks error message"
              >
                It looks like something has gone wrong while trying to delete a
                task.
              </ng-container>
              <ng-container
                *ngIf="formErr.action === TaskErrorAction.TASK_EDIT"
                i18n="Tasks error message"
              >
                It looks like something has gone wrong while trying to edit a
                task.
              </ng-container>
              <ng-container
                *ngIf="formErr.action === TaskErrorAction.TASK_LOAD"
                i18n="Tasks error message"
              >
                It looks like something has gone wrong while trying to load your
                tasks.
              </ng-container>
              <ng-container
                *ngIf="formErr.action === TaskErrorAction.TASK_LOAD_SPECIFIC"
                i18n="Tasks error message"
              >
                It looks like something has gone wrong while trying to load a
                specific task.
              </ng-container>
              <ng-container
                *ngIf="formErr.action === TaskErrorAction.TASK_SAVE"
                i18n="Tasks error message"
              >
                It looks like something has gone wrong while trying to save your
                task.
              </ng-container>
            </ng-container>
            <ng-container [ngSwitch]="formErr.remedy">
              <ng-container
                *ngSwitchCase="TaskErrorRemedy.REFRESH"
                i18n="Task error suggestion"
              >
                Please try refreshing the page.
              </ng-container>
              <ng-container *ngSwitchDefault i18n="Task error suggestion">
                Please try again.
              </ng-container>
            </ng-container>
            <fl-bit>
              <ng-container i18n="Task error support contact">
                If the problem persists,
                <fl-link
                  flTrackingLabel="ErrorSupportLink"
                  [link]="formErr.link"
                >
                  contact support.
                </fl-link>
              </ng-container>
              <ng-container
                *ngIf="formErr.code && formErr.code !== 'UNKNOWN_ERROR'"
                i18n="Task error code"
              >
                The code for this error is {{ formErr.code }}.
              </ng-container>
            </fl-bit>
          </fl-banner-alert>
        </ng-container>
        <fl-grid [ngClass]="{ FreelancerViewGrid: freelancerView }">
          <fl-col
            [colDesktopSmall]="
              freelancerView ? colDesktopFreelancer : colDesktopList
            "
          >
            <app-task-list
              [disableCreateBtn]="disableCreateBtn"
              [editingTaskId]="editingTaskId"
              [groupedTaskListCollection]="todoTaskListCollection"
              [listTitle]="TaskStatusApi.IN_PROGRESS"
              [padding]="!freelancerView"
              [showCreateBtn]="true"
              (selected)="itemSelectedHandler($event)"
              (newTask)="createNewTaskHandler()"
              (toggled)="formCloseHandler()"
              (hideError)="hideError()"
              (error)="taskListErrorHandler($event)"
            ></app-task-list>
            <app-task-list
              [editingTaskId]="editingTaskId"
              [groupedTaskListCollection]="completedTaskListCollection"
              [listTitle]="TaskStatusApi.CLOSED_RESOLVED"
              [padding]="!freelancerView"
              (selected)="itemSelectedHandler($event)"
              (newTask)="createNewTaskHandler()"
              (toggled)="formCloseHandler()"
              (hideError)="hideError()"
              (error)="taskListErrorHandler($event)"
            ></app-task-list>
          </fl-col>
          <fl-col
            [ngClass]="{ 'FreelancerViewGrid-form': freelancerView }"
            [colDesktopSmall]="
              freelancerView ? colDesktopFreelancer : colDesktopForm
            "
          >
            <app-task-form-change
              [currentTask]="currentTask"
              [isDraftProject]="isDraftProject"
              [isNewTask]="isNewTask"
              [projectId]="projectId$ | async"
              [showForm]="showForm"
              [taskListCollection]="currentTaskListCollection"
              [userId]="authUId$ | async"
              [isCleanState]="isCleanState"
              [freelancerView]="freelancerView"
              (taskEdited)="updateDraftTask($event)"
              (cleanStateChange)="setCurrentTaskCleanState($event)"
              (error)="taskListErrorHandler($event)"
              (hideError)="hideError()"
              (closeForm)="formCloseHandler()"
            ></app-task-form-change>
          </fl-col>
        </fl-grid>
      </fl-container>
    </fl-bit>
  `,
  styleUrls: ['./tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TasksComponent implements OnInit {
  BackgroundColor = BackgroundColor;
  BannerAlertType = BannerAlertType;
  LayoutType = LayoutType;
  Margin = Margin;
  TaskErrorAction = TaskErrorAction;
  TaskErrorRemedy = TaskErrorRemedy;
  TaskStatusApi = TaskStatusApi;

  @Input() projectId$: Rx.Observable<number>;
  @Input() freelancerView = false;

  authUId$: Rx.Observable<number>;
  colDesktopForm = 4;
  colDesktopFreelancer = 11;
  colDesktopList = 8;
  completedTaskListCollection: DatastoreCollection<TasksCollection>;
  currentTask?: Partial<Task>;
  currentTaskListCollection?: DatastoreCollection<TasksCollection>;
  disableCreateBtn = false;
  editingTaskId?: number;
  formError$?: Rx.BehaviorSubject<TaskError>;
  isDraftProject = false;
  isCleanState = false;
  isNewTask = false;
  pageSize = ContainerSize.DESKTOP_LARGE;
  projectId: number;
  showForm = false;
  todoTaskListCollection: DatastoreCollection<TasksCollection>;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private modalService: ModalService,
    private ref: ChangeDetectorRef,
    private storageService: LocalStorage,
  ) {}

  ngOnInit() {
    // limits the size of the tasklist container div on the old stack
    // remove once migration is completed
    if (this.freelancerView) {
      this.pageSize = ContainerSize.TABLET;
    }

    this.authUId$ = this.auth.getUserId().pipe(map(userId => toNumber(userId)));

    const taskSource$ = this.projectId$.pipe(
      map(projectId => {
        this.projectId = projectId;
        return { id: projectId, type: TaskSourceTypeApi.PROJECT };
      }),
    );

    // get all completed tasks
    this.completedTaskListCollection = this.datastore.collection<
      TasksCollection
    >('tasks', query =>
      query
        .where('source', '==', taskSource$)
        .where('status', '==', TaskStatusApi.CLOSED_RESOLVED),
    );

    // get all to do tasks
    this.todoTaskListCollection = this.datastore.collection<TasksCollection>(
      'tasks',
      query =>
        query
          .where('source', '==', taskSource$)
          .where('status', '==', TaskStatusApi.IN_PROGRESS),
    );

    this.currentTaskListCollection = this.todoTaskListCollection;

    this.setupDraftProject();
  }

  // Checks if the user has a draft task, if they do, open it.
  setupDraftProject(): void {
    const draftTaskState = this.storageService.get(TASK_LIST_KEY);
    if (!draftTaskState) {
      return;
    }

    const { draftTask, currentTaskClean } = draftTaskState;
    if (!draftTask) {
      return;
    }

    const isDraftProjectNew = !(draftTask && !!draftTask.id);
    this.showItem(draftTask, isDraftProjectNew, true, !!currentTaskClean);
  }

  // Checks if the currently open task has been edited.
  isCurrentTaskClean(): boolean {
    const draftTaskState = this.storageService.get(TASK_LIST_KEY);
    if (!draftTaskState) {
      return true;
    }

    return !!draftTaskState.currentTaskClean;
  }

  updateDraftTask(task: Partial<Task> | Task): void {
    const draftTaskState = this.storageService.get(TASK_LIST_KEY);

    this.storageService.set(TASK_LIST_KEY, {
      ...draftTaskState,
      draftTask: task,
    });
  }

  clearDraftTask(): void {
    const draftTaskState = this.storageService.get(TASK_LIST_KEY);

    this.storageService.set(TASK_LIST_KEY, {
      ...draftTaskState,
      draftTask: undefined,
    });
  }

  setCurrentTaskCleanState(state: boolean): void {
    const draftTaskState = this.storageService.get(TASK_LIST_KEY);

    this.storageService.set(TASK_LIST_KEY, {
      ...draftTaskState,
      currentTaskClean: state,
    });
  }

  // Called when an item is selected in the item list
  itemSelectedHandler(selected: Partial<Task>) {
    // prompt the user if he touched stuff in the form
    // to discard or keep changes
    if (!this.isCurrentTaskClean()) {
      this.modalService
        .open(TaskModalComponent, {
          inputs: {
            action: TaskModalAction.UNSAVED_CHANGES,
            projectId: this.projectId,
          },
          closeable: false,
          size: ModalSize.SMALL,
        })
        .afterClosed()
        .toPromise()
        .then(response => {
          if (response === TaskModalResult.CANCEL) {
            this.setCurrentTaskCleanState(true);
            this.clearDraftTask();
            this.showItem(selected);
          }
        });
    } else {
      this.clearDraftTask();
      this.showItem(selected);
    }
  }

  // Called when the close task form is clicked
  formCloseHandler() {
    if (!this.isCurrentTaskClean()) {
      this.modalService
        .open(TaskModalComponent, {
          inputs: {
            action: TaskModalAction.UNSAVED_CHANGES,
            projectId: this.projectId,
          },
          closeable: false,
          size: ModalSize.SMALL,
        })
        .afterClosed()
        .toPromise()
        .then(response => {
          if (response === TaskModalResult.CANCEL) {
            this.closeForm();
          }
        });
    } else {
      this.closeForm();
    }
  }

  // Called to create a new task
  createNewTaskHandler() {
    if (!this.isCurrentTaskClean()) {
      this.modalService
        .open(TaskModalComponent, {
          inputs: {
            action: TaskModalAction.UNSAVED_CHANGES,
            projectId: this.projectId,
          },
          closeable: false,
          size: ModalSize.SMALL,
        })
        .afterClosed()
        .toPromise()
        .then(response => {
          if (response === TaskModalResult.CANCEL) {
            this.createItem();
          }
        });
    } else {
      this.createItem();
    }
  }

  createItem() {
    this.currentTask = undefined;
    this.isNewTask = true;
    this.showForm = true;
    this.setCurrentTaskCleanState(true);
    this.hideError();
    this.disableCreateBtn = true;
    this.ref.markForCheck();
  }

  showItem(
    selected: Partial<Task>,
    isNewTask = false,
    isDraftProject = false,
    isCurrentTaskClean = true,
  ) {
    this.setCurrentTaskCleanState(isCurrentTaskClean);
    // if an item was selected while on create mode,
    // display a modal asking if the user wants to keep editing or not
    this.showForm = true;
    this.currentTask = selected;
    this.isDraftProject = isDraftProject;
    this.isCleanState = isCurrentTaskClean;

    if (selected.status === TaskStatusApi.IN_PROGRESS) {
      this.currentTaskListCollection = this.todoTaskListCollection;
    } else {
      this.currentTaskListCollection = this.completedTaskListCollection;
    }

    this.isNewTask = isNewTask;
    this.editingTaskId = selected.id;
    this.hideError();
    this.disableCreateBtn = isNewTask;
    this.ref.markForCheck();
  }

  closeForm() {
    this.showForm = false;
    this.isNewTask = false;
    this.setCurrentTaskCleanState(true);
    this.editingTaskId = undefined;
    this.disableCreateBtn = false;
  }

  taskListErrorHandler(err: TaskError) {
    this.formError$ = new Rx.BehaviorSubject<TaskError>(err);
  }

  hideError() {
    this.formError$ = undefined;
  }
}
